/**
 * Display settings — §14.1, §14.2, §14.3.
 *
 * NONE of these bump `audioEpoch`. Restarting the recitation because the theme changed
 * is not a behaviour anyone asked for, and it is the single reason this slice takes
 * `set` rather than the `edit` the other two take.
 */
import { FONT_SCALE_DEFAULT, FONT_SCALE_MAX, FONT_SCALE_MIN } from '@mushaf/corpus'
import type { DisplayMode, DisplaySlice, SettingsSet, ThemeId } from './types'

export const THEMES: { id: ThemeId; label: string }[] = [
  { id: 'cream', label: 'Paper' },
  { id: 'sepia', label: 'Sepia' },
  { id: 'white', label: 'White' },
  { id: 'green', label: 'Green' },
  { id: 'dark', label: 'Night' },
]

/**
 * The hints matter more than the labels. `word` is APPROXIMATE and `text` DOES NOT MATCH
 * THE PRINTED PAGE — a reader who cannot read the script has no way to notice either
 * from looking, so each mode says what it costs.
 */
export const DISPLAY_MODES: { id: DisplayMode; label: string; hint: string }[] = [
  { id: 'glyph', label: 'Mushaf (black and white)', hint: 'The original mushaf typeface' },
  {
    id: 'tajweed',
    label: 'Mushaf with tajweed colour',
    hint: 'The KFGQPC colour font — exact, letter by letter',
  },
  { id: 'word', label: 'Word colour', hint: 'Approximate: one colour per word' },
  {
    id: 'text',
    label: 'Reflowed text with tajweed',
    hint: 'Text that reflows — the line breaks no longer match the printed page',
  },
]

const clampScale = (n: number) =>
  Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, Math.round(n / 5) * 5))

/**
 * Project the theme onto <html> — the `data-theme` attribute AND the browser's own
 * chrome colour.
 *
 * ── Why `theme-color` belongs here and nowhere else ─────────────────────────────
 *
 * `theme-color` was a hardcoded `#fbf6e9` in index.html and in the PWA manifest, written
 * once and never updated. On a phone that is not cosmetic: the meta tag colours the
 * status bar and the address bar, so in the Night theme the reader got a cream status bar
 * welded above a near-black page — and in `display: standalone`, where that strip is the
 * only chrome there is, it reads as a rendering fault.
 *
 * It is a SECOND PROJECTION OF THE SAME VALUE as `data-theme`, so by the rule that
 * governs the attribute (one owner, one writer) it has to be written in the same breath.
 * A subscriber would be a second writer and would go out of step.
 *
 * ── Why the colour is READ rather than listed ───────────────────────────────────
 *
 * The obvious implementation is a `Record<ThemeId, string>` of hexes here. That would be
 * a fifth copy of the palette — after themes.css, the manifest, index.html's pre-paint
 * default, and whatever `pnpm contrast` parses — and L19's audit reads the STYLESHEET, so
 * a drifted copy here would be invisible to it. Instead the value is read back off the
 * element after the attribute lands, which makes themes.css the only place a theme colour
 * is ever written. Same reasoning as the tajweed exemplars: a hand-copied value silently
 * stops matching the data it claims to quote.
 *
 * `--surface` and not `--paper`: the chrome sits flush against the top bar, which is
 * `bg-[var(--surface)]`. `--paper` is the mushaf card, an inch further down.
 */
export function applyTheme(theme: ThemeId): void {
  document.documentElement.dataset.theme = theme

  const paint = () => {
    const surface = getComputedStyle(document.documentElement).getPropertyValue('--surface').trim()
    // Empty means the stylesheet has not applied yet. Nothing to write, and guessing a
    // hex is the duplication this function exists to avoid — so try again next frame.
    if (!surface) return false
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', surface)
    return true
  }

  // `main.tsx` imports app.css before this module, so the first call normally succeeds.
  // The retry covers the one case it cannot: a browser that has not committed the
  // stylesheet by the time module evaluation reaches here.
  if (!paint()) requestAnimationFrame(paint)
}

export const createDisplaySlice = (set: SettingsSet): DisplaySlice => ({
  theme: 'cream',
  mode: 'glyph',
  fontScale: FONT_SCALE_DEFAULT,

  /**
   * The <html> attribute is written HERE, not by a subscriber: the attribute is a
   * projection of this one value and giving it a second writer is how it goes out of
   * step with the store. `./index.ts` applies it once at module scope for first paint.
   *
   * `applyTheme` also carries the browser chrome's `theme-color`, which is the same
   * projection and so has the same single writer.
   */
  setTheme: (theme) => {
    applyTheme(theme)
    set({ theme })
  },
  setMode: (mode) => set({ mode }),
  setFontScale: (fontScale) => set({ fontScale: clampScale(fontScale) }),
})
